/**
 * Custom Fields and Groups. CRM-BUILD item 3's missing screen.
 *
 * ┌─ ⛔ ITEM 3 CALLS THESE TABLES "THE ROOT OF THE PHASE" AND NOTHING FILLED ───┐
 * │   THEM.                                                                     │
 * │ Every custom field in this product came from `src/dev/seed.ts`. A deployment │
 * │ that had not run it had a field engine with no fields - and the engine is    │
 * │ read by the record view, the list's column chooser, the board's card slots   │
 * │ and the filter engine. **Fourth instance in one phase of a table whose only  │
 * │ writer is the seeder**, after `partners`, pipelines and bookings.           │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ WHAT THIS SCREEN DOES NOT DO, SAID ON THE SCREEN RATHER THAN ONLY HERE:
 *   - CONDITIONAL VISIBILITY. The columns and the evaluation are item 20's and
 *     are built; an editor for it is a field-references-another-field builder
 *     with its own cycle check, and it is not here.
 *   - COMPUTED FIELDS. §4.7: *"a computed field must be answered where its
 *     inputs live"*, and nothing has decided what the expression language is.
 *
 * ⚠️ BOTH ARE STATED RATHER THAN HALF-BUILT. A type this screen cannot create is
 * offered in the list and refused with a reason, so the gap is visible where
 * somebody looks for the feature rather than only in a document.
 */
import { useEffect, useState } from 'react'
import {
  createFieldDefinition,
  createFieldGroup,
  fetchFieldGroups,
  purgeFieldDefinition,
  renameFieldDefinition,
  setFieldRetired,
  type FieldGroup,
} from '../data/new-booking-api.js'
import { ConfirmDialog } from '../ConfirmDialog.js'

/** ⚠️ MIRRORS `FIELD_TYPES` ON THE SERVER, WHICH MIRRORS THE SCHEMA'S CHECK.
 *  Three copies would be two too many, so the server's list is the one a test
 *  compares against `pg_constraint`; this one only decides what the dropdown
 *  offers and is refused by the server if it drifts. */
const CREATABLE = [
  { key: 'short_text', label: 'Short Text' },
  { key: 'long_text', label: 'Long Text' },
  { key: 'numeric', label: 'Numeric' },
  { key: 'currency', label: 'Currency' },
  { key: 'toggle', label: 'Toggle' },
  { key: 'date', label: 'Date' },
  { key: 'birthday', label: 'Birthday' },
  { key: 'datetime', label: 'Date and Time' },
  { key: 'url', label: 'URL' },
  { key: 'select', label: 'Select' },
  { key: 'radio', label: 'Radio' },
  { key: 'multiselect', label: 'Multiselect' },
] as const

/**
 * ┌─ ⛔ THE THREE TYPES THIS SCREEN CANNOT CREATE ARE OFFERED AND DISABLED ─────┐
 * │ Found 2026-09-16 by the refusal sweep, and it is a claim I made that the    │
 * │ build did not deliver: the ledger says *"a type the screen cannot create is │
 * │ still offered in the list and refused with a reason, so the gap is met      │
 * │ where somebody looks for the feature."* **The dropdown omitted them**, so   │
 * │ `type-not-creatable-here` was a refusal no person could reach.              │
 * │                                                                            │
 * │ ⚠️ SAME SHAPE AS `orphanTargetFor`: a client that satisfies a guard before   │
 * │ anybody meets it, with every assertion about the guard still passing -      │
 * │ `a-rule-asserted-where-it-is-enforced-is-not-a-rule-anybody-meets`.         │
 * │                                                                            │
 * │ ⛔ DISABLED RATHER THAN ABSENT is the same call the Automations tab took:    │
 * │ a capability the product has underneath and cannot configure reads as        │
 * │ unavailable, not as non-existent.                                          │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
const NOT_CREATABLE_YET = [
  { key: 'file', label: 'File', why: 'needs an upload control' },
  { key: 'computed', label: 'Computed', why: 'needs an expression language nobody has chosen' },
  { key: 'linked_entity', label: 'Linked Record', why: 'needs a target entity' },
] as const

const NEEDS_OPTIONS = new Set(['select', 'radio', 'multiselect'])

export function FieldAdmin({
  owner,
  onClose,
}: {
  owner: 'contact' | 'booking'
  onClose: () => void
}) {
  const [groups, setGroups] = useState<FieldGroup[] | null>(null)
  const [groupLabel, setGroupLabel] = useState('')
  const [target, setTarget] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [fieldType, setFieldType] = useState<string>('short_text')
  const [inDateSet, setInDateSet] = useState(false)
  const [options, setOptions] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [purging, setPurging] = useState<{ id: string; label: string; count: number } | null>(null)

  const load = async () => setGroups(await fetchFieldGroups(owner))
  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner])

  const addGroup = async () => {
    setProblem(null)
    const r = await createFieldGroup(owner, groupLabel)
    if (!r.ok) return setProblem(sentenceFor(r))
    setGroupLabel('')
    await load()
  }

  const addField = async () => {
    if (target === null) return
    setProblem(null)
    const r = await createFieldDefinition(target, {
      label,
      fieldType,
      inDateSet,
      ...(NEEDS_OPTIONS.has(fieldType)
        ? { options: options.split('\n').map((s) => s.trim()).filter((s) => s !== '') }
        : {}),
    })
    if (!r.ok) return setProblem(sentenceFor(r))
    setLabel('')
    setOptions('')
    setInDateSet(false)
    await load()
  }

  const toggleRetired = async (id: string, retired: boolean) => {
    setProblem(null)
    const r = await setFieldRetired(id, retired)
    if (!r.ok) return setProblem(sentenceFor(r))
    await load()
  }

  const purge = async () => {
    if (purging === null) return
    const r = await purgeFieldDefinition(purging.id)
    setPurging(null)
    if (!r.ok) return setProblem(sentenceFor(r))
    await load()
  }

  return (
    <div
      className="board__editor-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Custom Fields and Groups"
      data-testid="field-admin"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div className="board__editor">
        <h2 className="board__editor-title">
          Custom Fields on {owner === 'booking' ? 'Bookings' : 'Contacts'}
        </h2>

        <ul className="board__editor-slots" data-testid="field-groups">
          {(groups ?? []).map((g) => (
            <li key={g.id} className="board__editor-slot" data-group={g.id}>
              <span className="board__editor-toggle">
                <strong>{g.label}</strong>
                <span className="board__editor-count">
                  {g.fields.length === 1 ? '1 field' : `${g.fields.length} fields`}
                </span>
              </span>
            </li>
          ))}
          {(groups ?? []).flatMap((g) =>
            g.fields.map((f) => (
              <li
                key={f.id}
                className="board__editor-slot"
                data-field={f.id}
                data-retired={f.retiredAt !== null}
              >
                <span className="board__editor-toggle">
                  {f.label}
                  <span className="board__editor-required"> {f.fieldType}</span>
                  {/** ⛔ THE WIREFRAME'S OWN SENTENCE: *"Matomo ID · RETIRED ·
                   *  Visible on 214 existing bookings"*. Item 19's rule is that a
                   *  retired field stays on records that HOLD a value, so the
                   *  count is what makes RETIRED mean something. */}
                  {f.retiredAt !== null && (
                    <span className="board__editor-required" data-testid={`retired-${f.id}`}>
                      {' '}
                      RETIRED · visible on {f.valueCount}{' '}
                      {f.valueCount === 1 ? 'record' : 'records'}
                    </span>
                  )}
                </span>
                <span className="board__editor-move">
                  <button
                    type="button"
                    data-testid={`field-retire-${f.id}`}
                    onClick={() => void toggleRetired(f.id, f.retiredAt === null)}
                  >
                    {f.retiredAt === null ? 'Retire' : 'Restore'}
                  </button>
                  {/** ⛔ PURGE IS OFFERED ONLY ON A RETIRED FIELD, which is item
                   *  19's order: retire is reversible and purge is not, so the
                   *  irreversible one is not reachable in one click. */}
                  {f.retiredAt !== null && (
                    <button
                      type="button"
                      data-testid={`field-purge-${f.id}`}
                      onClick={() =>
                        setPurging({ id: f.id, label: f.label, count: f.valueCount })
                      }
                    >
                      Delete
                    </button>
                  )}
                </span>
              </li>
            )),
          )}
        </ul>

        <label className="board__editor-toggle" htmlFor="field-group-label">
          New Group
          <input
            id="field-group-label"
            data-testid="field-group-label"
            value={groupLabel}
            maxLength={60}
            onChange={(e) => setGroupLabel(e.target.value)}
          />
          <button type="button" data-testid="field-group-add" onClick={() => void addGroup()}>
            Add
          </button>
        </label>

        <label className="board__editor-toggle" htmlFor="field-target">
          New Field In
          <select
            id="field-target"
            data-testid="field-target"
            value={target ?? ''}
            onChange={(e) => setTarget(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">Choose a group…</option>
            {(groups ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        </label>

        <label className="board__editor-toggle" htmlFor="field-label">
          Name
          <input
            id="field-label"
            data-testid="field-label"
            value={label}
            maxLength={60}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <label className="board__editor-toggle" htmlFor="field-type">
          Type
          <select
            id="field-type"
            data-testid="field-type"
            value={fieldType}
            onChange={(e) => setFieldType(e.target.value)}
          >
            {CREATABLE.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
            {NOT_CREATABLE_YET.map((t) => (
              <option key={t.key} value={t.key} disabled data-testid={`field-type-${t.key}`}>
                {t.label} - not yet, {t.why}
              </option>
            ))}
          </select>
        </label>

        {NEEDS_OPTIONS.has(fieldType) && (
          <label className="board__editor-toggle" htmlFor="field-options">
            Options, one per line
            <textarea
              id="field-options"
              data-testid="field-options"
              rows={4}
              value={options}
              onChange={(e) => setOptions(e.target.value)}
            />
          </label>
        )}

        {/** ⛔ `in_date_set` IS NOT `field_type = 'date'`, and the label says why:
         *  the date set is what identifies a booking and what New Booking asks
         *  for. A date field that is not in the set is just a date. */}
        {owner === 'booking' && (
          <label className="board__editor-toggle">
            <input
              type="checkbox"
              checked={inDateSet}
              data-testid="field-in-date-set"
              onChange={(e) => setInDateSet(e.target.checked)}
            />
            Part of the booking&apos;s date set
            <span className="board__editor-note">
              Asked for when a booking is created, and shown on its card.
            </span>
          </label>
        )}

        <button type="button" data-testid="field-add" disabled={target === null} onClick={() => void addField()}>
          Add Field
        </button>

        {/** ⛔ THE TWO THINGS THIS SCREEN CANNOT DO, WHERE SOMEBODY LOOKS FOR
         *  THEM. A gap stated only in a document is a gap nobody meets. */}
        <p className="board__editor-note" data-testid="field-admin-gaps">
          Conditional visibility and computed fields are not editable here yet. A field can be created,
          renamed, retired and deleted; showing one only when another has a certain value, and fields
          calculated from other fields, are not built.
        </p>

        {problem !== null && (
          <p className="board__editor-error" role="alert" data-testid="field-problem">
            {problem}
          </p>
        )}

        <div className="board__editor-actions">
          <button type="button" onClick={onClose} data-testid="field-admin-close">
            Close
          </button>
        </div>
      </div>

      {/** ⛔ PURGE IS THE ONE CONTROL HERE THAT CANNOT BE UNDONE, and the
       *  confirm carries the count because that is what makes it a decision. */}
      {purging !== null && (
        <ConfirmDialog
          title="Delete This Field?"
          body={
            purging.count === 0
              ? `"${purging.label}" holds no values. Deleting it cannot be undone.`
              : `"${purging.label}" holds values on ${purging.count} ${
                  purging.count === 1 ? 'record' : 'records'
                }, and deleting it destroys them. This cannot be undone.`
          }
          confirmLabel="Delete It"
          cancelLabel="Keep It"
          onConfirm={() => void purge()}
          onCancel={() => setPurging(null)}
        />
      )}
    </div>
  )
}

function sentenceFor(r: { reason: string; message?: string; given?: string }): string {
  switch (r.reason) {
    case 'needs-options':
      return 'A select needs at least one option, or it renders a dropdown nobody can use.'
    case 'type-not-creatable-here':
      return `${r.given} fields are real but cannot be created from this screen yet.`
    case 'unknown-type':
      return `${r.given} is not a field type.`
    case 'label-required':
      return 'Every group and field needs a name, up to 60 characters.'
    case 'purge-blocked':
      return r.message ?? 'Something still depends on this field.'
    case 'not-found':
      return 'That no longer exists. Close this and reopen it.'
    default:
      return 'That could not be saved.'
  }
}
